import React, { useState, useEffect } from 'react';
import { Button, Modal, Field, Input, Message, Toast, Groupper, Header, Image, Table, Toolbar, Icon } from 'react-frontier';
import { Link } from 'react-router-dom';
import { EmploymentIncidenceSummary, SetLoading, UserClass, EmploymentSchedule, EmploymentScheduleType, UserEmployment } from '../SiteClasses';
import { bindClick } from '../SiteUtil';
import { useUser } from '../hooks';
import { SearchUserModal, UserScheduleSelector } from './index';
import UserAccess from '../UserAccess';
import classNames from 'classnames';
import moment from 'moment';
import API from '../API';
import EmploymentIncidenceModal from './EmploymentIncidenceModal';

interface EmploymentWorkersProps{
	employment_id: number;
	readonly?: boolean;
}

var DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

enum Modals{
	ASSIGN = 1,
	EDIT = 2,
	USERS = 3,
	REMOVE = 4,
	INCIDENCE = 5,
}

interface ScheduleConflictEmployment{
	employment_id: number;
	employment_name: string;
}

var EmploymentWorkers = (props: EmploymentWorkersProps)=>{
	var { hasAccess } = useUser();
	var [users, setUsers] = useState<UserEmployment []>(null);
	var [schedules, setSchedules] = useState<EmploymentSchedule []>(null);
	var [loadingSchedules, setLoadingSchedules] = useState<boolean>(false);
	var [selectedUser, setSelectedUser] = useState<UserEmployment>(null);
	var [selectedScheduleIds, setSelectedScheduleIds] = useState<number[]>([]);
	var [selectedScheduleTypes, setSelectedScheduleTypes] = useState<{ [scheduleId: number]: number }>({});
	var [shownModal, setShownModal] = useState<number>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [scheduleConflictEmployment, setScheduleConflictEmployment] = useState<ScheduleConflictEmployment>(null);

	useEffect(()=>{
		API.getEmploymentUsers(props.employment_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setUsers(res.data);
		}).catch(()=>{
			Toast.error('Hubo un error cargando los trabajadores (LCL-1)');
		});
	}, [props.employment_id]);

	var getSchedules = ()=>{
		setLoadingSchedules(true);
		return API.getEmploymentSchedules(props.employment_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setSchedules(res.data);
		}).catch(()=>{
			Toast.error('Hubo un error cargando los horarios (LCL-1)')
		}).finally(()=> {
			setLoadingSchedules(false)
		});
	};

	var showAssignModal = ()=>{
		if(props.readonly) return;
		if(!schedules && !loadingSchedules) getSchedules();
		setSelectedUser(null);
		setSelectedScheduleIds([]);
		setSelectedScheduleTypes({});
		setModalPrompts([]);
		setScheduleConflictEmployment(null);
		setShownModal(Modals.ASSIGN);
	};

	var showEditModal = (user: UserEmployment)=>{
		if(!schedules && !loadingSchedules) getSchedules();
		setSelectedUser(user);
		setSelectedScheduleIds(user.schedule_ids);
		setSelectedScheduleTypes((user.schedule_ids || []).reduce((acc, schedule_id, index) => {
			acc[schedule_id] = user.schedule_type_ids?.[index] || EmploymentScheduleType.WORK;
			return acc;
		}, {} as { [scheduleId: number]: number }));
		setModalPrompts([]);
		setScheduleConflictEmployment(null);
		setShownModal(Modals.EDIT);
	};

	var onUserSelected = (user: UserClass)=>{
		if(users.find(u=> u.user_id === user.user_id)) return Toast.error('El usuario ya está asignado como empleado.');
		setSelectedUser(user as UserEmployment);
		setSelectedScheduleIds([]);
		setSelectedScheduleTypes({});
		setModalPrompts([]);
		setScheduleConflictEmployment(null);
		setShownModal(Modals.ASSIGN);
	};

	var toggleSchedule = (scheduleId: number)=>{
		setScheduleConflictEmployment(null);
		var arr = [...selectedScheduleIds];
		var ix = arr.indexOf(scheduleId);
		if(ix === -1) {
			arr.push(scheduleId);
			setSelectedScheduleTypes(prev => ({ ...prev, [scheduleId]: prev[scheduleId] || EmploymentScheduleType.WORK }));
		}else{
			arr.splice(ix, 1);
			setSelectedScheduleTypes(prev => {
				var next = { ...prev };
				delete next[scheduleId];
				return next;
			});
		}
		setSelectedScheduleIds(arr);
	};

	var onScheduleTypeChange = (scheduleId: number, type_id: number)=>{
		setScheduleConflictEmployment(null);
		setSelectedScheduleTypes(prev => ({ ...prev, [scheduleId]: type_id }));
	};

	var assignSchedulesToUser = (setLoading: SetLoading)=>{
		if(!selectedUser) return setModalPrompts(['Selecciona un usuario']);
		if(selectedScheduleIds.length === 0) return setModalPrompts(['Selecciona al menos un horario']);
		var schedule_type_ids = selectedScheduleIds.map(id => selectedScheduleTypes[id] || EmploymentScheduleType.WORK);

		setLoading(true);
		API.assignSchedulesToUser(selectedUser.user_id, selectedScheduleIds, schedule_type_ids).then(res=>{
			if(res.error){
				setScheduleConflictEmployment(res.data?.conflict || null);
				return setModalPrompts([res.message]);
			}
			Toast.success(`Horarios asignados a ${selectedUser.name}`);

			var days = [...new Set((schedules || []).filter(s=> selectedScheduleIds.includes(s.schedule_id)).map(s=> s.day))];
			setUsers(prev => {
				var idx = prev.findIndex(u=> u.user_id === selectedUser.user_id);
				if(idx !== -1){
					var next = [...prev];
					next[idx] = { ...next[idx], days, schedule_ids: selectedScheduleIds, schedule_type_ids };
					return next;
				}
				return [...prev, { ...selectedUser, days, schedule_ids: selectedScheduleIds, schedule_type_ids, incidences: [] } as UserEmployment];
			});

			setShownModal(null);
			setSelectedUser(null);
			setSelectedScheduleIds([]);
			setSelectedScheduleTypes({});
			setScheduleConflictEmployment(null);
		}).catch(()=>{
			setScheduleConflictEmployment(null);
			setModalPrompts(['Hubo un error asignando los horarios (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var updateSchedulesToUser = (setLoading: SetLoading)=>{
		if(!selectedUser) return setModalPrompts(['Selecciona un usuario']);
		if(selectedScheduleIds.length === 0) return setModalPrompts(['Selecciona al menos un horario']);
		var schedule_type_ids = selectedScheduleIds.map(id => selectedScheduleTypes[id] || EmploymentScheduleType.WORK);

		setLoading(true);
		API.assignSchedulesToUser(selectedUser.user_id, selectedScheduleIds, schedule_type_ids).then(res=>{
			if(res.error){
				setScheduleConflictEmployment(res.data?.conflict || null);
				return setModalPrompts([res.message]);
			}
			Toast.success(`Horarios actualizados para ${selectedUser.name}`);

			var days = [...new Set((schedules || []).filter(s=> selectedScheduleIds.includes(s.schedule_id)).map(s=> s.day))];
			setUsers(prev => {
				var idx = prev.findIndex(u=> u.user_id === selectedUser.user_id);
				if(idx !== -1){
					var next = [...prev];
					next[idx] = { ...next[idx], days, schedule_ids: selectedScheduleIds, schedule_type_ids };
					return next;
				}
				return prev;
			});

			setShownModal(null);
			setSelectedUser(null);
			setSelectedScheduleIds([]);
			setSelectedScheduleTypes({});
			setScheduleConflictEmployment(null);
		}).catch(()=>{
			setScheduleConflictEmployment(null);
			setModalPrompts(['Hubo un error actualizando los horarios (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var removeEmployee = (setLoading: SetLoading)=>{
		if(!selectedUser) return setModalPrompts(['Selecciona un usuario']);

		setLoading(true);
		API.removeEmployee(selectedUser.user_id, props.employment_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success(`Empleado ${selectedUser.name} eliminado del empleo`);

			setUsers(prev => prev.filter(u=> u.user_id !== selectedUser.user_id));

			setShownModal(null);
			setSelectedUser(null);
			setSelectedScheduleIds([]);
			setSelectedScheduleTypes({});
		}).catch(()=>{
			setModalPrompts(['Hubo un error eliminando el empleado (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var showRemoveModal = ()=>{
		setShownModal(Modals.REMOVE);
	};

	var showIncidenceModal = ()=>{
		if(!selectedUser) return;
		if(!schedules && !loadingSchedules) getSchedules();
		setShownModal(Modals.INCIDENCE);
	};

	var onIncidencesCreated = (incidences: EmploymentIncidenceSummary[])=>{
		var todayStartUnix = moment().startOf('day').unix();
		var visibleIncidences = incidences.filter(incidence => incidence.date_start >= todayStartUnix);
		if(visibleIncidences.length > 0){
			setUsers(prev => prev.map(user => user.user_id === selectedUser.user_id ? {
				...user,
				incidences: [...(user.incidences || []), ...visibleIncidences].sort((a, b)=> a.date_start - b.date_start)
			} : user));
			setSelectedUser(prev => prev ? {
				...prev,
				incidences: [...(prev.incidences || []), ...visibleIncidences].sort((a, b)=> a.date_start - b.date_start)
			} : prev);
		}
		setShownModal(Modals.EDIT);
	};

	var onIncidenceDeleted = (log_id: number)=>{
		setUsers(prev => prev.map(user => user.user_id === selectedUser.user_id ? {
			...user,
			incidences: (user.incidences || []).filter(incidence => incidence.log_id !== log_id)
		} : user));
		setSelectedUser(prev => prev ? {
			...prev,
			incidences: (prev.incidences || []).filter(incidence => incidence.log_id !== log_id)
		} : prev);
	};

	var assignedSchedules = (schedules || [])
		.filter(schedule => selectedUser?.schedule_ids?.includes(schedule.schedule_id))
		.map(schedule => ({
			...schedule,
			type_id: selectedUser?.schedule_type_ids?.[selectedUser.schedule_ids.indexOf(schedule.schedule_id)] || EmploymentScheduleType.WORK,
		}))
		.sort((a, b)=> a.day - b.day || a.time_start - b.time_start);

	if(!users) return <Header loading text='Cargando trabajadores' style={{ marginTop: 20 }} />;
	return (
		<div className='ar employment-screen'>
			<Groupper
				title='Empleados'
				width={600}
				style={{ marginTop: 20 }}
				fitted
				titleRight={
					hasAccess(UserAccess.EMPLOYMENTS.EDIT_EMPLOYMENTS) && !props.readonly && (
						<Button text="Asignar" iconName="plus-circle" color="black" onClick={bindClick(showAssignModal, true)} />
					)
				}
			>
				<div className='users-container'>
					{users.length > 0 ? users.map(u=>(
						<div className='user-item' onClick={bindClick(showEditModal, u)} key={u.user_id}>
							<div className='avatar'>
								<Image src={'/placeholder.png'} fallback='/placeholder.png' height={70} style={{ borderRadius: 4 }}/>
							</div>
							<div className='user-info'>
								<div>
									<div>{u.name.split(' ')[0]}</div>
									<div>{u.name.split(' ').slice(1).join(' ')}</div>
								</div>
							</div>
							<div className='user-days'>
								{DAYS.map((day, di)=> (
									<div
										key={di}
										className={classNames('day-indicator', {
											'assigned': u.days.includes(di),
											'unassigned': !u.days.includes(di)
										})}
									>
										{day.charAt(0)}
									</div>
								))}
							</div>
						</div>
					)) : (
					<div style={{ padding: 10 }}>
						<Header style={{ marginTop: 30 }} text='No hay empleados asignados' subtext='Asigna empleados para que puedan registrar su asistencia.' iconName='user-clock' size='small'/>
					</div>
				)}
				</div>
			</Groupper>
			<Modal open={shownModal === Modals.ASSIGN} onClose={bindClick(setShownModal, null)} size='tiny'>
				<Modal.Header>Asignar empleado</Modal.Header>
				<Modal.Content>
					<Field label='Trabajador'>
						<Input
							value={selectedUser ? `${selectedUser.name} (${selectedUser.email})` : ''}
							placeholder='Selecciona un trabajador...'
							readonly
							button={
								<Button
									text='Seleccionar'
									onClick={()=> setShownModal(Modals.USERS)}
								/>
							}
						/>
					</Field>
					{loadingSchedules ? (
						<Header loading text='Cargando horarios...' />
					) : selectedUser && <UserScheduleSelector
						schedules={schedules}
						selectedScheduleIds={selectedScheduleIds}
						selectedScheduleTypes={selectedScheduleTypes}
						onScheduleToggle={toggleSchedule}
						onScheduleTypeChange={onScheduleTypeChange}
						dayNames={DAYS}
					/>}
					{modalPrompts.length > 0 && (
						<Message type="error" style={{ marginTop: 15 }}>
							<ul style={{ margin: 0, padding: 0, listStyleType: 'none' }}>
								{modalPrompts.map((prompt, index) => (
									<li key={index}>{prompt}</li>
								))}
								{scheduleConflictEmployment && (
									<li>
										<Link
											to={`/employments/${scheduleConflictEmployment.employment_id}/workers`}
											target='_blank'
											style={{ display: 'inline-flex', alignItems: 'baseline', color: 'currentcolor',gap: 6, textDecoration: 'none', marginTop: 15 }}
										>
											{scheduleConflictEmployment.employment_name}
											<Icon size={14} name='external-link' style={{ margin: 0 }} />
										</Link>
									</li>
								)}
							</ul>
						</Message>
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cancelar' basic onClick={bindClick(setShownModal, null)} />
					<Button text='Guardar' color='black' onClick={assignSchedulesToUser} />
				</Modal.Actions>
			</Modal>
			<Modal open={shownModal === Modals.EDIT} onClose={bindClick(setShownModal, null)} size='tiny'>
				<Modal.Header>Editar empleado</Modal.Header>
					<Modal.Content style={{ padding: 0, overflow: 'visible' }}>
						<Toolbar stretch>
							<Toolbar.Item text="Incidencia" iconName='calendar-times' onClick={showIncidenceModal}/>
							<Toolbar.Item text="Eliminar" iconName='trash' onClick={showRemoveModal}/>
						</Toolbar>
						<Table
							details
							striped
							data={selectedUser && [
								['ID de usuario', selectedUser.user_id],
								['Nombre', selectedUser.name],
								['Correo electrónico', selectedUser.email]
							]}
							/>
						{loadingSchedules ? (
							<Header loading text='Cargando horarios...' />
						) : selectedUser && <>
							<UserScheduleSelector
								schedules={schedules}
								selectedScheduleIds={selectedScheduleIds}
								selectedScheduleTypes={selectedScheduleTypes}
								onScheduleToggle={toggleSchedule}
								onScheduleTypeChange={onScheduleTypeChange}
								dayNames={DAYS}
							/>
						</>}
					{modalPrompts.length > 0 && (
						<Message type="error">
							<ul style={{ margin: 0, padding: 0, listStyleType: 'none' }}>
								{modalPrompts.map((prompt, index) => (
									<li key={index}>{prompt}</li>
								))}
								{scheduleConflictEmployment && (
									<li>
										<Link
											to={`/employments/${scheduleConflictEmployment.employment_id}/workers`}
											target='_blank'
											style={{ display: 'inline-flex', alignItems: 'baseline', color: 'currentcolor',gap: 6, textDecoration: 'none', marginTop: 15 }}
										>
											{scheduleConflictEmployment.employment_name}
											<Icon size={14} name='external-link' style={{ margin: 0 }} />
										</Link>
									</li>
								)}
							</ul>
						</Message>
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cancelar' basic onClick={bindClick(setShownModal, null)} />
					<Button text='Guardar' color='black' onClick={updateSchedulesToUser} />
				</Modal.Actions>
			</Modal>
			<SearchUserModal
				open={shownModal === Modals.USERS}
				onClose={()=> setShownModal(Modals.ASSIGN)}
				title='Seleccionar trabajador'
				onSelected={onUserSelected}
			/>
				<Modal open={shownModal === Modals.REMOVE} onClose={bindClick(setShownModal, Modals.EDIT)} size='tiny'>
				<Modal.Header>Eliminar empleado</Modal.Header>
				<Modal.Content>
					<Header text={`¿Eliminar empleado?`}  subtext={'El empleado dejará de formar parte de este empleo por completo.'}/>
					<Table
						details
						striped
						style={{ marginTop: 20 }}
						data={selectedUser && [
							['ID de usuario', selectedUser.user_id],
							['Nombre', selectedUser.name],
							['Correo electrónico', selectedUser.email],
							['Horarios', selectedUser.schedule_ids?.length]
						]}
					/>
					<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cancelar' basic onClick={bindClick(setShownModal, Modals.EDIT)} />
					<Button text='Eliminar' color='red' onClick={removeEmployee} />
				</Modal.Actions>
				</Modal>
			<EmploymentIncidenceModal
				open={shownModal === Modals.INCIDENCE}
				onClose={bindClick(setShownModal, Modals.EDIT)}
				employment_id={props.employment_id}
				user={selectedUser}
				assignedSchedules={assignedSchedules}
				loadingSchedules={loadingSchedules}
				dayNames={DAYS}
				onCreated={onIncidencesCreated}
				onDeleted={onIncidenceDeleted}
			/>
			</div>
		);
	};

export default EmploymentWorkers;
